import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { reportService, UserReport, ReportStatus, ReportPageResponse } from '../../services/reportService';
import { postService, Post } from '../../services/postService';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { AlertTriangle, CheckCircle2, XCircle, Clock, Eye, FileText, Image as ImageIcon } from 'lucide-react';

export const AdminReportsPage: React.FC = () => {
  const [reports, setReports] = useState<UserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<UserReport | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'resolve' | 'reject'>('resolve');
  const [actionNote, setActionNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'ALL'>('ALL');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewing: 0,
    resolved: 0,
    rejected: 0
  });
  const [postCache, setPostCache] = useState<Record<number, Post>>({});
  const [loadingPosts, setLoadingPosts] = useState<Set<number>>(new Set());
  const pageSize = 20;

  useEffect(() => {
    loadReports();
  }, [currentPage, statusFilter]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const filterStatus = statusFilter === 'ALL' ? undefined : statusFilter;
      const response: ReportPageResponse<UserReport> = await reportService.getReports(currentPage, pageSize, filterStatus);
      const reportsData = response.content || [];
      
      // Auto-load posts for POST type reports
      reportsData.forEach(report => {
        if (report.targetType === 'POST' && !postCache[report.targetId] && !loadingPosts.has(report.targetId)) {
          loadPost(report.targetId);
        }
      });
      
      setReports(reportsData);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
      
      // Load all reports to calculate statistics
      if (statusFilter === 'ALL') {
        const allReports: ReportPageResponse<UserReport> = await reportService.getReports(0, 1000);
        const allContent = allReports.content || [];
        setStats({
          total: allContent.length,
          pending: allContent.filter(r => (r.status as string) === 'PENDING' || r.status === ReportStatus.PENDING).length,
          reviewing: allContent.filter(r => (r.status as string) === 'REVIEWING' || r.status === ReportStatus.REVIEWING).length,
          resolved: allContent.filter(r => (r.status as string) === 'RESOLVED' || r.status === ReportStatus.RESOLVED).length,
          rejected: allContent.filter(r => (r.status as string) === 'REJECTED' || r.status === ReportStatus.REJECTED).length
        });
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể tải danh sách báo cáo');
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedReport) return;
    try {
      setError(null);
      await reportService.resolveReport(selectedReport.id, actionNote.trim() || 'Đã xử lý báo cáo');
      setShowActionModal(false);
      setActionNote('');
      setSelectedReport(null);
      await loadReports();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể xử lý báo cáo');
      console.error('Failed to resolve report:', err);
    }
  };

  const handleReject = async () => {
    if (!selectedReport) return;
    try {
      setError(null);
      await reportService.rejectReport(selectedReport.id, actionNote.trim() || 'Không có vi phạm');
      setShowActionModal(false);
      setActionNote('');
      setSelectedReport(null);
      await loadReports();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể từ chối báo cáo');
      console.error('Failed to reject report:', err);
    }
  };

  const openActionModal = (report: UserReport, type: 'resolve' | 'reject') => {
    setSelectedReport(report);
    setActionType(type);
    setActionNote('');
    setShowActionModal(true);
  };

  const getStatusIcon = (status: string | ReportStatus) => {
    const statusStr = status.toString().toUpperCase();
    switch (statusStr) {
      case 'PENDING':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'REVIEWING':
        return <Eye className="w-5 h-5 text-blue-600" />;
      case 'RESOLVED':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'DISMISSED':
        return <XCircle className="w-5 h-5 text-gray-600" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string | ReportStatus) => {
    const statusStr = status.toString().toUpperCase();
    const colorMap: Record<string, string> = {
      'PENDING': 'bg-yellow-100 text-yellow-800 border border-yellow-300',
      'REVIEWING': 'bg-blue-100 text-blue-800 border border-blue-300',
      'RESOLVED': 'bg-green-100 text-green-800 border border-green-300',
      'REJECTED': 'bg-red-100 text-red-800 border border-red-300',
      'DISMISSED': 'bg-gray-100 text-gray-800 border border-gray-300'
    };
    const colorClass = colorMap[statusStr] || 'bg-gray-100 text-gray-800';
    const statusText = statusStr === 'PENDING' ? 'Đang chờ xử lý' :
                       statusStr === 'REVIEWING' ? 'Đang xem xét' :
                       statusStr === 'RESOLVED' ? 'Đã xử lý' :
                       statusStr === 'REJECTED' ? 'Đã từ chối' :
                       statusStr === 'DISMISSED' ? 'Đã bỏ qua' : statusStr;
    return (
      <span className={`px-3 py-1 ${colorClass} text-xs font-semibold rounded-full inline-flex items-center gap-1`}>
        {getStatusIcon(status)}
        {statusText}
      </span>
    );
  };

  const loadPost = async (postId: number) => {
    if (postCache[postId] || loadingPosts.has(postId)) return;
    
    try {
      setLoadingPosts(prev => new Set(prev).add(postId));
      const post = await postService.getPost(postId);
      setPostCache(prev => ({ ...prev, [postId]: post }));
    } catch (err) {
      console.error('Failed to load post:', err);
    } finally {
      setLoadingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Quản lý báo cáo</h1>
            <p className="text-gray-600 mt-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Xem và xử lý các báo cáo vi phạm từ người dùng
            </p>
          </div>
          <button
            onClick={loadReports}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Làm mới
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-200 shadow-sm hover:shadow transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-blue-500 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-full">Tổng số</span>
            </div>
            <p className="text-3xl font-bold text-blue-900 mb-1">{stats.total}</p>
            <p className="text-sm text-blue-700 font-medium">Tất cả báo cáo</p>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-5 border border-yellow-200 shadow-sm hover:shadow transition-shadow cursor-pointer" onClick={() => { setStatusFilter(ReportStatus.PENDING); setCurrentPage(0); }}>
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-yellow-500 rounded-lg">
                <Clock className="w-5 h-5 text-white" />
              </div>
              {stats.pending > 0 && (
                <span className="text-xs font-bold text-white bg-red-500 px-2 py-1 rounded-full animate-pulse">!</span>
              )}
            </div>
            <p className="text-3xl font-bold text-yellow-900 mb-1">{stats.pending}</p>
            <p className="text-sm text-yellow-700 font-medium">Chờ xử lý</p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-5 border border-blue-200 shadow-sm hover:shadow transition-shadow cursor-pointer" onClick={() => { setStatusFilter(ReportStatus.REVIEWING); setCurrentPage(0); }}>
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-blue-500 rounded-lg">
                <Eye className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-blue-900 mb-1">{stats.reviewing}</p>
            <p className="text-sm text-blue-700 font-medium">Đang xem xét</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-5 border border-green-200 shadow-sm hover:shadow transition-shadow cursor-pointer" onClick={() => { setStatusFilter(ReportStatus.RESOLVED); setCurrentPage(0); }}>
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-green-500 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-green-900 mb-1">{stats.resolved}</p>
            <p className="text-sm text-green-700 font-medium">Đã xử lý</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-5 border border-red-200 shadow-sm hover:shadow transition-shadow cursor-pointer" onClick={() => { setStatusFilter(ReportStatus.REJECTED); setCurrentPage(0); }}>
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-red-500 rounded-lg">
                <XCircle className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-red-900 mb-1">{stats.rejected}</p>
            <p className="text-sm text-red-700 font-medium">Đã từ chối</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Lọc theo trạng thái:
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setStatusFilter('ALL');
                  setCurrentPage(0);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  statusFilter === 'ALL'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                }`}
              >
                <span className="flex items-center gap-2">
                  Tất cả
                  <span className={`${
                    statusFilter === 'ALL' ? 'bg-white/20' : 'bg-gray-100'
                  } px-2 py-0.5 rounded-full text-xs font-bold`}>{stats.total}</span>
                </span>
              </button>
              {Object.values(ReportStatus).map((status) => {
                const count = status === ReportStatus.PENDING ? stats.pending :
                              status === ReportStatus.REVIEWING ? stats.reviewing :
                              status === ReportStatus.RESOLVED ? stats.resolved :
                              status === ReportStatus.REJECTED ? stats.rejected : 0;
                const isActive = statusFilter === status;
                return (
                  <button
                    key={status}
                    onClick={() => {
                      setStatusFilter(status);
                      setCurrentPage(0);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? status === ReportStatus.PENDING ? 'bg-yellow-500 text-white shadow-md' :
                          status === ReportStatus.REVIEWING ? 'bg-blue-500 text-white shadow-md' :
                          status === ReportStatus.RESOLVED ? 'bg-green-500 text-white shadow-md' :
                          status === ReportStatus.REJECTED ? 'bg-red-500 text-white shadow-md' :
                          'bg-gray-600 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {reportService.getStatusText(status)}
                      {count > 0 && (
                        <span className={`${
                          isActive ? 'bg-white/20' : 'bg-gray-100'
                        } px-2 py-0.5 rounded-full text-xs font-bold`}>
                          {count}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-gray-800"></div>
                <AlertTriangle className="w-6 h-6 text-gray-700 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
              <span className="mt-4 text-gray-600 font-medium">Đang tải báo cáo...</span>
            </div>
          </div>
        ) : reports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-16 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-gray-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Không có báo cáo nào</h3>
              <p className="text-gray-600 leading-relaxed">
                {statusFilter === 'ALL'
                  ? 'Hiện tại không có báo cáo nào trong hệ thống. Đây là dấu hiệu tốt!'
                  : `Không có báo cáo với trạng thái "${reportService.getStatusText(statusFilter as ReportStatus)}"`}
              </p>
              {statusFilter !== 'ALL' && (
                <button
                  onClick={() => { setStatusFilter('ALL'); setCurrentPage(0); }}
                  className="mt-6 px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 shadow-sm hover:shadow font-medium"
                >
                  Xem tất cả báo cáo
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Reports List */}
            <div className="space-y-4">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow transition-all duration-200"
                >
                  {/* Header with Report Type */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-500 rounded-lg">
                          <AlertTriangle className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-blue-900">
                            Báo cáo {report.targetType === 'POST' ? 'Bài viết' : report.targetType === 'COMMENT' ? 'Bình luận' : 'Người dùng'} #{report.id}
                          </h3>
                          <p className="text-sm text-blue-700 mt-0.5">
                            {reportService.getReasonText(report.reason)}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(report.status)}
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Reporter Info Section - Highlighted */}
                    <div className="mb-5 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 bg-orange-500 rounded-lg">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-bold text-orange-900 uppercase">Người báo cáo</h4>
                      </div>
                      <div className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-orange-200 shadow-sm">
                        {report.reporterAvatarUrl ? (
                          <img src={report.reporterAvatarUrl} alt="" className="w-14 h-14 rounded-full border-3 border-orange-400 shadow-md" />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-xl border-3 border-orange-500 shadow-md">
                            {(report.reporterName || report.reporterUsername)?.[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-lg font-bold text-orange-900">{report.reporterName || report.reporterUsername || `User ${report.reporterId}`}</p>
                          <p className="text-sm text-orange-700 font-medium">ID: {report.reporterId}</p>
                          {report.reporterUsername && (
                            <p className="text-xs text-orange-600">@{report.reporterUsername}</p>
                          )}
                          <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Báo cáo {formatDistanceToNow(new Date(report.createdAt), { locale: vi, addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Original Post Display */}
                    {report.targetType === 'POST' && (
                      <div className="mb-5 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-purple-500 rounded-lg">
                            <FileText className="w-4 h-4 text-white" />
                          </div>
                          <h4 className="text-sm font-bold text-purple-900 uppercase">Bài viết gốc</h4>
                          {!postCache[report.targetId] && !loadingPosts.has(report.targetId) && (
                            <button
                              onClick={() => loadPost(report.targetId)}
                              className="ml-auto text-xs text-purple-600 hover:text-purple-800 font-medium underline"
                            >
                              Tải bài viết
                            </button>
                          )}
                        </div>
                        {loadingPosts.has(report.targetId) ? (
                          <div className="bg-white rounded-lg p-4 border border-purple-200 text-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent mx-auto"></div>
                            <p className="text-xs text-purple-600 mt-2">Đang tải...</p>
                          </div>
                        ) : postCache[report.targetId] ? (
                          <div className="bg-white rounded-lg p-4 border-2 border-purple-200 shadow-sm">
                            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-purple-100">
                              {postCache[report.targetId].authorAvatarUrl && postCache[report.targetId].authorAvatarUrl !== null ? (
                                <img src={postCache[report.targetId].authorAvatarUrl!} alt="" className="w-10 h-10 rounded-full border-2 border-purple-300" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold border-2 border-purple-300">
                                  {postCache[report.targetId].authorDisplayName?.[0] || postCache[report.targetId].authorUsername?.[0] || '?'}
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-bold text-purple-900">{postCache[report.targetId].authorDisplayName || postCache[report.targetId].authorUsername}</p>
                                <p className="text-xs text-purple-600">@{postCache[report.targetId].authorUsername}</p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-800 leading-relaxed mb-3 whitespace-pre-wrap">{postCache[report.targetId].content}</p>
                            {postCache[report.targetId].media && postCache[report.targetId].media!.length > 0 && (
                              <div className="grid grid-cols-2 gap-2 mb-3">
                                {postCache[report.targetId].media!.slice(0, 4).map((media, idx) => (
                                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border-2 border-purple-200">
                                    {media.mediaType === 'image' ? (
                                      <img src={media.mediaUrl} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full bg-purple-100 flex items-center justify-center">
                                        <ImageIcon className="w-8 h-8 text-purple-400" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-4 text-xs text-purple-600 pt-2 border-t border-purple-100">
                              <span>❤️ {postCache[report.targetId].reactionCount || 0}</span>
                              <span>💬 {postCache[report.targetId].commentCount || 0}</span>
                              <span>📅 {formatDistanceToNow(new Date(postCache[report.targetId].createdAt), { locale: vi, addSuffix: true })}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white rounded-lg p-4 border border-purple-200">
                            <p className="text-sm text-gray-600 mb-2">Nội dung bị báo cáo:</p>
                            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{report.targetContent || 'Không có nội dung'}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Violator Info Section */}
                    {(report.targetAuthorUsername || (report.targetContent && report.targetType !== 'POST')) && (
                      <div className="mb-5 bg-gradient-to-br from-red-50 to-rose-50 border-l-4 border-red-400 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="p-1.5 bg-red-500 rounded-lg">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <h4 className="text-sm font-bold text-red-900 uppercase">Nội dung vi phạm</h4>
                        </div>
                        {report.targetAuthorUsername && (
                          <div className="flex items-center gap-3 mb-3 p-3 bg-white rounded-lg border-2 border-red-200">
                            {report.targetAuthorAvatarUrl ? (
                              <img src={report.targetAuthorAvatarUrl} alt="" className="w-10 h-10 rounded-full border-2 border-red-300" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-red-200 flex items-center justify-center text-red-700 font-bold border-2 border-red-300">
                                {report.targetAuthorDisplayName?.[0] || report.targetAuthorUsername?.[0] || '?'}
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-semibold text-red-900">{report.targetAuthorDisplayName || report.targetAuthorUsername}</p>
                              <p className="text-xs text-red-600">@{report.targetAuthorUsername} • ID: {report.targetAuthorId}</p>
                            </div>
                          </div>
                        )}
                        {report.targetContent && report.targetType !== 'POST' && (
                          <div className="bg-white rounded-lg p-4 border-2 border-red-200">
                            <p className="text-xs font-semibold text-red-700 uppercase mb-2">Nội dung bị báo cáo:</p>
                            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                              {report.targetContent}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mb-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {/* Target Type */}
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            <span className="text-xs font-semibold text-gray-600 uppercase">Đối tượng</span>
                          </div>
                          <span className="text-sm font-bold text-gray-900">
                            {report.targetType === 'POST' ? 'Bài viết' : report.targetType === 'COMMENT' ? 'Bình luận' : 'Người dùng'}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">ID: {report.targetId}</p>
                        </div>

                        {/* Date */}
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center gap-2 mb-1">
                            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs font-semibold text-gray-600 uppercase">Thời gian</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {formatDistanceToNow(new Date(report.createdAt), {
                              locale: vi,
                              addSuffix: true
                            })}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(report.createdAt).toLocaleDateString('vi-VN')}
                          </p>
                        </div>

                        {(report.resolvedAt || report.reviewedAt) && (
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="flex items-center gap-2 mb-1">
                              <CheckCircle2 className="w-4 h-4 text-gray-600" />
                              <span className="text-xs font-semibold text-gray-600 uppercase">Đã xử lý</span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {formatDistanceToNow(new Date(report.resolvedAt || report.reviewedAt!), {
                                locale: vi,
                                addSuffix: true
                              })}
                            </span>
                            {report.resolvedByName && (
                              <p className="text-xs text-gray-500 mt-1">Bởi: {report.resolvedByName}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Reporter's Description */}
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                        </svg>
                        <p className="text-sm font-bold text-gray-900 uppercase">Lý do báo cáo từ người dùng</p>
                      </div>
                      <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-gray-400">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gray-200 rounded-lg">
                            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-800 leading-relaxed font-medium">
                              "{report.description}"
                            </p>
                            <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              <span>Đã báo cáo {formatDistanceToNow(new Date(report.createdAt), { locale: vi, addSuffix: true })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Admin Note / Resolution */}
                    {(report.resolution || report.adminNote) && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm font-semibold text-gray-700 uppercase">Ghi chú từ admin</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-400">
                          <p className="text-sm text-gray-800 leading-relaxed font-medium">
                            {report.resolution || report.adminNote}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
                      {/* View Original Link */}
                      {report.targetType !== 'USER' && (
                        <a
                          href={report.targetType === 'POST' ? `/posts/${report.targetId}` : `/posts/${report.targetId}#comment`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200 font-medium border border-gray-300"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Xem {report.targetType === 'POST' ? 'bài viết' : 'bình luận'} gốc
                        </a>
                      )}
                      
                      {(report.status === ReportStatus.PENDING || (report.status as string) === 'PENDING') && (
                        <div className="flex gap-3">
                          <Button
                            onClick={() => openActionModal(report, 'resolve')}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white transition-all duration-200 font-semibold py-3 rounded-lg shadow-sm hover:shadow"
                          >
                            <CheckCircle2 className="w-5 h-5 mr-2" />
                            Chấp nhận và xử lý
                          </Button>
                          <Button
                            onClick={() => openActionModal(report, 'reject')}
                            variant="outline"
                            className="flex-1 border-2 border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 transition-all duration-200 font-semibold py-3 rounded-lg"
                          >
                            <XCircle className="w-5 h-5 mr-2" />
                            Từ chối
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Trang {currentPage + 1} / {totalPages}
                    </p>
                    <p className="text-xs text-gray-600">
                      Tổng cộng: <span className="font-bold text-gray-900">{totalElements}</span> báo cáo
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handlePreviousPage}
                    disabled={currentPage === 0}
                    variant="outline"
                    className="px-5 py-2.5 border border-gray-300 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium rounded-lg"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Trước
                  </Button>
                  <Button
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages - 1}
                    variant="outline"
                    className="px-5 py-2.5 border border-gray-300 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium rounded-lg"
                  >
                    Sau
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Modal */}
        <Modal
          isOpen={showActionModal}
          onClose={() => {
            setShowActionModal(false);
            setSelectedReport(null);
            setActionNote('');
          }}
          title={actionType === 'resolve' ? 'Xử lý báo cáo' : 'Từ chối báo cáo'}
        >
          <div className="space-y-5">
            {selectedReport && (
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5 text-gray-700" />
                  <h4 className="text-sm font-bold text-gray-900 uppercase">Thông tin báo cáo</h4>
                </div>
                <div className="space-y-2.5">
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">ID Báo cáo</p>
                    <p className="text-sm font-bold text-gray-900">#{selectedReport.id}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Lý do</p>
                    <p className="text-sm font-medium text-gray-900">{reportService.getReasonText(selectedReport.reason)}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Mô tả</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{selectedReport.description}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Ghi chú {actionType === 'resolve' ? 'giải pháp' : 'lý do từ chối'}
              </label>
              <textarea
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder={
                  actionType === 'resolve'
                    ? 'Ví dụ: Đã xóa bài viết vi phạm và cảnh cáo người dùng'
                    : 'Ví dụ: Nội dung không vi phạm quy định cộng đồng'
                }
                className="w-full p-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 resize-none transition-all duration-200 text-sm"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Ghi chú này sẽ được lưu lại và hiển thị trong hệ thống
              </p>
            </div>

            <div className="flex gap-3 pt-5 border-t border-gray-200">
              <Button
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedReport(null);
                  setActionNote('');
                }}
                variant="outline"
                className="flex-1 border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 font-medium py-3 rounded-lg"
              >
                Hủy
              </Button>
              <Button
                onClick={actionType === 'resolve' ? handleResolve : handleReject}
                className={`flex-1 ${
                  actionType === 'resolve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                } text-white transition-all duration-200 font-semibold py-3 rounded-lg shadow-sm hover:shadow`}
              >
                {actionType === 'resolve' ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Xác nhận xử lý
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 mr-2" />
                    Xác nhận từ chối
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default AdminReportsPage;
